import { useMemo, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";

import { ListLoader, Icon } from "components/atoms";
import { StatementListItem } from "components/molecules";
import { ColumnSettingsModal } from "components/organisms";
import { useColumnPreferences } from "hooks/useColumnPreferences";

import { Statement } from "interfaces/statements";
import { IconName } from "interfaces/icons";

import { MAIN_THEME } from "styles/colors";
import { FONT_FAMILY_MEDIUM } from "styles/typography";
import {
	getStatementsCount,
	getStatementsList,
	getStatementsLoading,
	getStatementsTotal,
} from "state/modules/statements";

const List = styled(InfiniteScroll as any)`
	display: grid;
	grid-template-columns: 1fr;
	width: 100%;
	gap: 1px;
	border-bottom-right-radius: 16px;
	border-bottom-left-radius: 16px;
	overflow: hidden !important;
	background: transparent;
`;

const Container = styled.div`
	padding: 15px 30px 40px;
	overflow-y: auto;

	@media (max-width: 768px) {
		padding: 15px 10px 70px;
	}

	@media (max-width: 480px) {
		padding: 15px 5px 70px;
	}
`;

const ScrollWrapper = styled.div`
	width: 100%;
	overflow-x: auto;
	overflow-y: visible;
	
	/* Custom scrollbar styling */
	&::-webkit-scrollbar {
		height: 8px;
	}
	
	&::-webkit-scrollbar-track {
		background: ${MAIN_THEME.DARK_BG};
		border-radius: 4px;
	}
	
	&::-webkit-scrollbar-thumb {
		background: ${MAIN_THEME.DARK_BG_TERTIARY};
		border-radius: 4px;
		
		&:hover {
			background: ${MAIN_THEME.GREY};
		}
	}
`;

const TableWrapper = styled.div<{ mobileMinWidth: string }>`
	min-width: 1200px;

	@media (max-width: 768px) {
		min-width: ${(props) => props.mobileMinWidth};
	}
`;


const TableHeaderItem = styled.div`
	font-size: 14px;
		text-align: left;
		display: flex;
		justify-content: flex-start;
		align-items: center;
		color: ${MAIN_THEME.TEXT_SECONDARY};
		height: 38px;
		padding: 10px 8px;
		background: transparent;



		font-weight: 600;
		text-transform: none;
		letter-spacing: 0.3px;


		@media (max-width: 768px) {
			font-size: 12px;
			height: 36px;
			padding: 8px 6px;
		}

`;

const TableHeader = styled.div<{ columns?: string }>`
	display: grid;
	grid-template-columns: ${(props) => props.columns || "1fr 1fr 1fr 1fr 1fr 1fr 0.8fr"};
	gap: 1px;
	margin-bottom: 1px;
	border-top-right-radius: 16px;
	border-top-left-radius: 16px;
	overflow: hidden;
	color: ${MAIN_THEME.TEXT_SECONDARY};
	background: transparent;
	border-bottom: 1px solid ${MAIN_THEME.BORDER_COLOR};
`;

const SettingsButton = styled.button`
	border: none;
	background: transparent;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 10px;
	margin-left: auto;

	&:hover {
		opacity: 0.8;
	}
`;

const ContentHeader = styled.div`
	display: flex;
	justify-content: flex-end;
`;

const DEFAULT_COLUMNS = [
	{ id: 'property', label: 'Property', isVisible: true, width: '1fr' },
	{ id: 'period', label: 'Period', isVisible: true, width: '1fr' },
	{ id: 'reservations', label: 'Reservations', isVisible: true, width: '1fr' },
	{ id: 'income', label: 'Income', isVisible: true, width: '1fr' },
	{ id: 'expenses', label: 'Expenses', isVisible: true, width: '1fr' },
	{ id: 'paidOut', label: 'Paid Out', isVisible: true, width: '1fr' },
	{ id: 'createdAt', label: 'Created At', isVisible: true, width: '1fr' },
	{ id: 'viewInvoice', label: 'View Invoice', isVisible: true, width: '0.8fr' },
];

const EmptyListMessage = styled.p`
	font-family: ${FONT_FAMILY_MEDIUM};
	color: ${MAIN_THEME.TEXT_SECONDARY};
	font-size: 16px;
	background: ${MAIN_THEME.DARK_BG_TERTIARY};
	border: 1px solid ${MAIN_THEME.BORDER_COLOR};
	border-radius: 8px;
	padding: 15px;
	text-align: center;
`;

const LoaderContainer = styled.div`
	padding: 0;
`;

interface Props {
	showProperty?: boolean;
}

const StatementsList = (props: Props): JSX.Element => {
	const { showProperty } = props;
	const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

	const { columns, toggleColumn } = useColumnPreferences("statementsList", DEFAULT_COLUMNS);

	const visibleColumns = useMemo(() => {
		return columns.filter(col => {
			if (!showProperty && col.id === 'property') return false;
			return col.isVisible;
		});
	}, [columns, showProperty]);

	const visibleColumnIds = useMemo(() => visibleColumns.map(c => c.id), [visibleColumns]);

	const gridTemplateColumns = useMemo(() => {
		return visibleColumns.map(c => c.width).join(" ");
	}, [visibleColumns]);

	const mobileMinWidth = useMemo(() => {
		return `${Math.max(visibleColumns.length * 120, 100)}px`;
	}, [visibleColumns]);

	const isColumnVisible = (id: string) => visibleColumnIds.includes(id);

	const statements = useSelector(getStatementsList);

	const isLoading = useSelector(getStatementsLoading);
	const statementsCount = useSelector(getStatementsCount);
	const statementsTotal = useSelector(getStatementsTotal);

	const dispatch = useDispatch();

	const hasMore = useMemo(
		() => statementsCount < statementsTotal,
		[statementsCount, statementsTotal]
	);

	const loadMore = () => {
		if (!isLoading) {
			// dispatch();
			// getPropertyClaims({
			// 	propertyId,
			// 	isLoadMore: true,
			// })
		}
	};

	const renderLoader = () =>
		statementsCount <= 0 && isLoading ? (
			<LoaderContainer>
				<ListLoader />
			</LoaderContainer>
		) : null;

	const renderStatementsList = () => {
		if (statementsCount) {
			return (
				<List
					scrollableTarget="statementsContainer"
					dataLength={statementsCount}
					next={loadMore}
					hasMore={hasMore}
					loader={<ListLoader />}
				>
					{statements.map((statement: Statement) => (
						<StatementListItem
							data={statement}
							key={statement.id}
							showProperty={showProperty}
							visibleColumnIds={visibleColumnIds}
							gridTemplateColumns={gridTemplateColumns}
						/>
					))}
				</List>
			);
		}

		if (isLoading) {
			return renderLoader();
		}

		return <EmptyListMessage>Empty list</EmptyListMessage>;
	};

	const renderTableHeader = () =>
		statementsCount ? (
			<TableHeader columns={gridTemplateColumns}>
				{isColumnVisible('property') ? <TableHeaderItem>Property</TableHeaderItem> : null}
				{isColumnVisible('period') && <TableHeaderItem>Period</TableHeaderItem>}
				{isColumnVisible('reservations') && <TableHeaderItem>Reservations</TableHeaderItem>}
				{isColumnVisible('income') && <TableHeaderItem>Income</TableHeaderItem>}
				{isColumnVisible('expenses') && <TableHeaderItem>Expenses</TableHeaderItem>}
				{isColumnVisible('paidOut') && <TableHeaderItem>Paid Out</TableHeaderItem>}
				{isColumnVisible('createdAt') && <TableHeaderItem>Created At</TableHeaderItem>}
				{isColumnVisible('viewInvoice') && <TableHeaderItem>View Invoice</TableHeaderItem>}
			</TableHeader>
		) : null;

	return (
		<Container>
			<ContentHeader>
				<SettingsButton onClick={() => setIsSettingsModalOpen(true)}>
					<Icon name={IconName.SETTINGS} color={MAIN_THEME.TEXT_PRIMARY} size={20} />
				</SettingsButton>
			</ContentHeader>
			<ScrollWrapper>
				<TableWrapper mobileMinWidth={mobileMinWidth}>
					{renderTableHeader()}
					{renderStatementsList()}
				</TableWrapper>
			</ScrollWrapper>
			<ColumnSettingsModal
				isVisible={isSettingsModalOpen}
				close={() => setIsSettingsModalOpen(false)}
				columns={columns.filter(c => showProperty || c.id !== 'property')}
				onToggle={toggleColumn}
			/>
		</Container>
	);
};

export default StatementsList;
